/**
 * Social kickoff banner CONTENT + constants.
 *
 * Historically this module also rendered the banner to a PNG with the Canvas 2D
 * API. That render layer has been retired: the banner studio now renders each
 * template as real HTML/CSS (see `components/welcome/banner-templates/`) with
 * native editable text, and exports via html2canvas. Only the non-canvas pieces
 * live here now — the palettes/themes, the default copy, and the category/slug
 * helpers — which the studio, its templates, and `useBannerActions` consume.
 *
 * The filename is unchanged so existing imports (`prepareCategories`,
 * `slugifySchoolName`, the theme constants and shared types) keep resolving.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * A color scheme a banner can be rendered in. The first three are the original
 * variants (kept for back-compat with {@link BANNER_VARIANTS}); the rest are
 * additional bright themes offered by the studio's theme picker.
 */
export type BannerVariant =
  | 'blueTeal'
  | 'purplePink'
  | 'orangeAmber'
  | 'greenLime'
  | 'indigoSky'
  | 'roseCoral'
  | 'softWhite';

/**
 * The original three variants, in display order. Kept exported and unchanged for
 * back-compat. The studio uses {@link BANNER_COLOR_THEMES} for its fuller list.
 */
export const BANNER_VARIANTS: readonly BannerVariant[] = [
  'blueTeal',
  'purplePink',
  'orangeAmber',
] as const;

/**
 * Banner layout formats. Independent from color theme. Each format resolves to
 * its own intrinsic export dimensions (see {@link bannerDimensions}) and its own
 * DOM template component.
 *
 * - `values-showcase` — the square (1080x1080) values poster.
 * - `how-to-earn` — a square (1080x1080) "How to Earn Points" poster with
 *   fully editable text (eyebrow, title, three numbered cards).
 * - `rewards-showcase` — a square (1080x1080) "Rewards" poster with a grid of
 *   editable reward tiles (eyebrow, title, six emoji/name/cost tiles).
 * - `parent-letter` — a portrait (1700x2200, 8.5x11 @ 200 DPI) print-ready
 *   take-home letter to families, downloaded as a US Letter PDF.
 */
export type BannerFormat =
  | 'values-showcase'
  | 'how-to-earn'
  | 'rewards-showcase'
  | 'parent-letter';
export type BannerAudience = 'school' | 'class';

/** The default layout format. */
export const DEFAULT_BANNER_FORMAT: BannerFormat = 'values-showcase';

/** Pixel dimensions of the export for a given format. */
export interface BannerDimensions {
  width: number;
  height: number;
}

/**
 * Resolves the intrinsic export dimensions for a format. The three posters are
 * square 1080x1080; the `parent-letter` is portrait 1700x2200 (8.5x11 at 200
 * DPI). Templates render at these exact pixel sizes so html2canvas captures full
 * resolution.
 */
export function bannerDimensions(format: BannerFormat): BannerDimensions {
  if (format === 'parent-letter') {
    return { width: 1700, height: 2200 };
  }
  return { width: 1080, height: 1080 };
}

/**
 * A color theme as the studio's picker needs it: a stable id (the
 * {@link BannerVariant}), a human label, and two swatch colors for a small
 * preview gradient. Order here is the studio's display order.
 */
export interface BannerColorTheme {
  id: BannerVariant;
  label: string;
  /** Top swatch color (matches the banner's background gradient top). */
  swatchTop: string;
  /** Bottom swatch color (matches the banner's background gradient bottom). */
  swatchBottom: string;
}

/**
 * One numbered card in the "How to Earn Points" poster. `emoji` is fixed per
 * card (not editable); the two text lines are the user-editable content.
 */
export interface HowToEarnCard {
  /** Fixed decorative emoji shown beside the number badge. */
  emoji: string;
  /** Bold card headline (editable). */
  cardTitle: string;
  /** Lighter supporting line (editable). */
  cardSubtitle: string;
}

/** The full editable text payload for the `how-to-earn` format. */
export interface HowToEarnText {
  /** Small uppercase eyebrow above the title. */
  eyebrow: string;
  /** Large poster title. */
  title: string;
  /** Exactly three numbered cards. */
  cards: [HowToEarnCard, HowToEarnCard, HowToEarnCard];
}

/**
 * Seed copy for the how-to-earn poster. The studio lifts this into React state
 * so leaders can edit each line; the template renders whatever it is handed.
 * No em dashes per product copy rules.
 */
export function createDefaultHowToEarnText(
  audience: BannerAudience = 'class',
): HowToEarnText {
  return {
    eyebrow: `Our ${audience} runs on LiveSchool`,
    title: 'How to Earn Points',
    cards: [
      { emoji: '🙌', cardTitle: 'Do your best', cardSubtitle: 'Be kind, work hard, and help out.' },
      { emoji: '⭐', cardTitle: 'Earn points', cardSubtitle: 'Your teachers award points on the spot.' },
      { emoji: '🎁', cardTitle: 'Shop the store', cardSubtitle: 'Spend your points on rewards you love.' },
    ],
  };
}

export const DEFAULT_HOW_TO_EARN_TEXT: HowToEarnText =
  createDefaultHowToEarnText('class');

/** The editable text payload for the `rewards-showcase` format (eyebrow/title). */
export interface RewardsShowcaseText {
  /** Small uppercase eyebrow above the title. */
  eyebrow: string;
  /** Large poster title. */
  title: string;
}

/**
 * Which store a reward option comes from. `school` is the site-wide store;
 * `mine` is the current teacher's own store. Used to prioritize the school
 * store when searching, and to label options in the add-reward popover.
 */
export type RewardScope = 'school' | 'mine';

/**
 * A single real reward the poster can show, normalized from the rewards store.
 * Data-driven: leaders pick WHICH rewards show (remove/add), they don't edit a
 * reward's name or cost here.
 */
export interface RewardOption {
  /** Stable reward id from the store. */
  id: string;
  /** Reward name. */
  name: string;
  /** Point cost. */
  cost: number;
  /** Reward image URL (uploaded photo or the default star). */
  image: string;
  /** Which store this reward belongs to. */
  scope: RewardScope;
  /** Short human label for the store, shown in the add-reward popover. */
  storeLabel: string;
}

/** How many reward tiles the poster shows at most (a tidy 2x3 grid). */
export const MAX_SOCIAL_REWARDS = 6;

/**
 * Seed eyebrow/title for the rewards poster. No em dashes per product copy
 * rules.
 */
export function createDefaultRewardsShowcaseText(
  audience: BannerAudience = 'class',
): RewardsShowcaseText {
  return {
    eyebrow: `Our ${audience} runs on LiveSchool`,
    title: 'Rewards You Can Earn',
  };
}

export const DEFAULT_REWARDS_SHOWCASE_TEXT: RewardsShowcaseText =
  createDefaultRewardsShowcaseText('class');

/**
 * The default reward selection: the most expensive rewards in the SCHOOL store,
 * capped at {@link MAX_SOCIAL_REWARDS}. Falls back to any-scope options only if
 * there are no school rewards at all, so the poster is never empty.
 */
export function pickDefaultRewardIds(options: readonly RewardOption[]): string[] {
  const byCostDesc = (a: RewardOption, b: RewardOption) => b.cost - a.cost;
  const school = options.filter((o) => o.scope === 'school').sort(byCostDesc);
  const source = school.length > 0 ? school : [...options].sort(byCostDesc);
  return source.slice(0, MAX_SOCIAL_REWARDS).map((o) => o.id);
}

/** Formats a numeric point cost for display, e.g. `250 pts` / `1 pt`. */
export function formatRewardCost(cost: number): string {
  return `${cost.toLocaleString()} ${cost === 1 ? 'pt' : 'pts'}`;
}

// ---------------------------------------------------------------------------
// Palettes / themes
// ---------------------------------------------------------------------------

/** A small generic set so a brand-new rubric still produces a nice banner. */
export const FALLBACK_CATEGORIES: readonly string[] = [
  'Respect',
  'Responsibility',
  'Kindness',
  'Effort',
  'Teamwork',
  'Integrity',
] as const;

/** Hard cap on chips rendered, so the layout stays tidy. */
export const MAX_CATEGORIES = 8;

/**
 * Per-variant palette for the values-showcase poster. Backgrounds are vertical
 * gradients; chip/text colors are tuned for contrast. Consumed directly by
 * `ValuesShowcaseTemplate` (CSS `linear-gradient`, chip fills) and by
 * {@link BANNER_COLOR_THEMES} for the picker swatches.
 */
export interface Palette {
  /** Background gradient stops (top -> bottom). */
  bgTop: string;
  bgBottom: string;
  /** Rounded accent border color. */
  border: string;
  /** Primary text color (title + headline). */
  text: string;
  /** Title shadow; omit to use the standard dark-background treatment. */
  textShadow?: string;
  /** Chip fill + chip text. */
  chipBg: string;
  chipText: string;
  /** Optional alternate logo asset for light backgrounds. */
  logoSrc?: string;
}

export const PALETTES: Record<BannerVariant, Palette> = {
  blueTeal: {
    bgTop: '#0EA5E9',
    bgBottom: '#0D9488',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#0F766E',
  },
  purplePink: {
    bgTop: '#7C3AED',
    bgBottom: '#DB2777',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#9333EA',
  },
  orangeAmber: {
    bgTop: '#F97316',
    bgBottom: '#F59E0B',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#C2410C',
  },
  greenLime: {
    bgTop: '#10B981',
    bgBottom: '#65A30D',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#15803D',
  },
  indigoSky: {
    bgTop: '#4F46E5',
    bgBottom: '#0EA5E9',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#4338CA',
  },
  roseCoral: {
    bgTop: '#F43F5E',
    bgBottom: '#FB7185',
    border: '#FFFFFF',
    text: '#FFFFFF',
    chipBg: '#FFFFFF',
    chipText: '#BE123C',
  },
  softWhite: {
    bgTop: '#FFFFFF',
    bgBottom: '#F3F4F6',
    border: '#1D4ED8',
    text: '#1D4ED8',
    textShadow: '0 2px 6px rgba(29, 78, 216, 0.10)',
    chipBg: '#1D4ED8',
    chipText: '#FFFFFF',
    logoSrc: 'logo-blue.svg',
  },
};

/**
 * Selectable color themes for the studio's picker, in display order. Derived
 * from {@link PALETTES} so swatches always match the rendered banner.
 */
export const BANNER_COLOR_THEMES: readonly BannerColorTheme[] = [
  { id: 'blueTeal', label: 'Ocean', swatchTop: PALETTES.blueTeal.bgTop, swatchBottom: PALETTES.blueTeal.bgBottom },
  { id: 'purplePink', label: 'Berry', swatchTop: PALETTES.purplePink.bgTop, swatchBottom: PALETTES.purplePink.bgBottom },
  { id: 'orangeAmber', label: 'Sunset', swatchTop: PALETTES.orangeAmber.bgTop, swatchBottom: PALETTES.orangeAmber.bgBottom },
  { id: 'greenLime', label: 'Meadow', swatchTop: PALETTES.greenLime.bgTop, swatchBottom: PALETTES.greenLime.bgBottom },
  { id: 'indigoSky', label: 'Twilight', swatchTop: PALETTES.indigoSky.bgTop, swatchBottom: PALETTES.indigoSky.bgBottom },
  { id: 'roseCoral', label: 'Coral', swatchTop: PALETTES.roseCoral.bgTop, swatchBottom: PALETTES.roseCoral.bgBottom },
  { id: 'softWhite', label: 'White', swatchTop: PALETTES.softWhite.bgTop, swatchBottom: PALETTES.softWhite.bgBottom },
] as const;

// ---------------------------------------------------------------------------
// Category + filename helpers
// ---------------------------------------------------------------------------

/**
 * Normalizes raw rubric categories for banner display: sorts by `sort_order`,
 * maps to trimmed names, drops empties, dedupes (case-insensitive), and caps to
 * {@link MAX_CATEGORIES}. Falls back to {@link FALLBACK_CATEGORIES} when the
 * rubric is empty (brand-new sites).
 *
 * @param raw - Behavior categories from the core store (id/name/sort_order).
 * @param limit - Max names to return. Defaults to {@link MAX_CATEGORIES} so
 *   existing callers are unchanged; pass `Infinity` for the full rubric (used to
 *   populate the Parent Letter's "add behavior" chooser from every behavior, not
 *   just the first {@link MAX_CATEGORIES}).
 * @returns A clean, capped list of category names ready to render.
 */
export function prepareCategories(
  raw: Array<{ name?: string | null; sort_order?: number | null }>,
  limit: number = MAX_CATEGORIES,
): string[] {
  const sorted = [...raw].sort((a, b) => {
    const ao = a.sort_order ?? Number.MAX_SAFE_INTEGER;
    const bo = b.sort_order ?? Number.MAX_SAFE_INTEGER;
    return ao - bo;
  });

  const seen = new Set<string>();
  const names: string[] = [];
  for (const item of sorted) {
    const name = (item.name ?? '').trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    names.push(name);
    if (names.length >= limit) break;
  }

  return names.length > 0 ? names : [...FALLBACK_CATEGORIES];
}

/**
 * Slugifies a school name for download filenames (lowercase, hyphenated,
 * alphanumeric only). Falls back to `school` when nothing usable remains.
 *
 * @param name - The school display name.
 * @returns A filesystem-safe slug.
 */
export function slugifySchoolName(name: string): string {
  const slug = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'school';
}
